import type {
    SendSleepRecorderOption,
    SleepRecorderType,
} from '../../../../../../common/validation/customControls/somnology';
import type { IPatientContactInfoDraft } from '../../../../../../common/validation/patientContactInfo';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { IMedication } from '../../../../interfaces/medication';
import type { IServiceOrganizationSettings } from '../../../../interfaces/serviceOrganizationSettings';
import type { IEpisodeContext } from '../../../episodes/EpisodeContext';
import type { IRequestAddEdit } from '../../../requests/RequestItemAddEdit';
import type { ICustomControlRequestAddEditProps } from '../../interfaces/';
import type { IOsasc1CustomControlData } from '../../OSASC1/v1/Osasc1V1DiagnosticReportAddEditCustomControlFields';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { ComorbiditiesChoice } from '../../../../../../common/enums/comorbiditiesChoice';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ServiceCode } from '../../../../../../common/enums/serviceCode';
import { SomnologyOrganizationType } from '../../../../../../common/enums/somnologyOrganizationType';
import { ToastType } from '../../../../../../common/enums/toastType';
import { YesOrNo } from '../../../../../../common/enums/yesOrNo';
import { fetchJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { renameErrorKeys } from '../../../../../utils/renameErrorKeys';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form, FormDivider, HeaderDivider } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormSection } from '../../../../elements/forms/formSection/FormSection';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputControl } from '../../../../elements/forms/inputControl/InputControl';
import { PatientContactAddEdit } from '../../../../elements/PatientContactAddEdit';
import { CheckboxInput } from '../../../../fields/Checkbox';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { ReloadEpisode } from '../../../episodes/reloadEpisode';
import { ProfessionalSummaryData } from '../../../professionalSummaryData/ProfessionalSummaryData';
import {
    Osasc1V1DiagnosticReportAddEditCustomControlFields,
    osasc1V1DiagnosticReportInitializeCustomControlData,
} from '../../OSASC1/v1/Osasc1V1DiagnosticReportAddEditCustomControlFields';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { ContinueWithoutMedicationsModal } from '../../utils/v1/medications/ContinueWithoutMedicationsModal';
import { MedicationsAddEdit } from '../../utils/v1/medications/MedicationsAddEdit';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';
import { ServiceOrganizationProvider } from '../../utils/v1/ServiceOrganizationProvider';
import { OsasvV1FollowingSteps } from './OsasvV1FollowingSteps';
import { ReferralAddEditKsyosMedicalCenter } from './ReferralAddEditKsyosMedicalCenter';
import { RequestPatientPermission } from './RequestPatientPermission';

export interface ICustomControlDataOsasv {
    consult: IOsasc1CustomControlData | null;
    contactgegevens_patient: IPatientContactInfoDraft;
    hoofdklacht: string | null;
    osasReferralPermission: {
        patientPermission: boolean;
        patientQuestionnaireEmail: boolean;
    };
    patientHasSmartphone: boolean | null;
    relevante_klachten: {
        andersText: string | null;
        meer_dan_5_ademstops_per_uur: boolean;
        niet_verfrissende_slaap: boolean;
        stokkende_ademhaling_tijdens_de_slaap: boolean;
        veel_snurken: boolean;
        verminderde_concentratie: boolean;
        vermoeidheid_overdag: boolean;
    } | null;
    slaapapneu_in_de_1e_graad: 'Ja' | 'Nee' | 'Onbekend' | null;
    slaaprecorderVerzenden: SendSleepRecorderOption | null;
    somnologyOrganizationType: SomnologyOrganizationType | null;
    toestemmingVideoConsult?: boolean;
    typeSlaaprecorder: SleepRecorderType | null;
    verwijsreden: string | null;
    voorgeschiedenis: string | null;
}

interface IInnerOsasvV1RequestAddEditState {
    buttonDisabled?: boolean;
    proceedWithoutMedications?: boolean;
}

export function OsasvV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    const episodeContext = useEpisodeContext();

    return (
        <>
            {!props.request.customControlData.osasReferralPermission.patientPermission && (
                <RequestPatientPermission
                    onAccept={async (osasReferralPermission) =>
                        await props.handleChangeCustomControl({ osasReferralPermission })
                    }
                    serviceId={props.service.id}
                    {...props}
                    patient={episodeContext.patient}
                />
            )}

            <InnerOsasvV1RequestAddEdit
                {...props}
                request={{ ...props.request }}
                {...episodeContext}
            />
        </>
    );
}

class InnerOsasvV1RequestAddEdit extends Component<
    ICustomControlRequestAddEditProps & IEpisodeContext,
    IInnerOsasvV1RequestAddEditState
> {
    public async componentDidMount() {
        const update = { ...osasvV1InitializeCustomControlData(), ...this.props.request.customControlData };

        if (
            this.props.request.customControlData.somnologyOrganizationType ===
            SomnologyOrganizationType.ksyosMedicalCenter
        ) {
            const initializedConsult = osasc1V1DiagnosticReportInitializeCustomControlData();
            update.consult = {
                ...initializedConsult,
                ...this.props.request.customControlData.consult,
                comorbiditeiten: {
                    ...initializedConsult.comorbiditeiten,
                    ...this.props.request.customControlData.consult.comorbiditeiten,
                },
            };
        }

        if (this.props.patient.careProviderOrganizationId) {
            update.careProviderOrganizationId = this.props.patient.careProviderOrganizationId;
        }

        if (this.props.patient.careProviderPractitionerId) {
            update.careProviderPractitionerId = this.props.patient.careProviderPractitionerId;
        }

        await this.props.handleChangeCustomControl(update);
    }

    public render() {
        return (
            <NewOrEditRequestProcessContainer
                isFirstItem={this.props.isFirstItem}
                itemState={this.props.itemState}
                process={this.props.process}
                service={this.props.service}
                title={getServiceLabel(this.props.service.code, 'request', this.props.service.name)}
            >
                <Form onSubmit={async () => null}>
                    <CareProviderSelector
                        {...this.props}
                        automation="generalPractitioner"
                        entity={this.props.request}
                    />

                    <ServiceOrganizationProvider
                        checkRecipientIsOwnOrganization
                        errors={this.props.errors}
                        initialOrganizationId={this.props.request.selectedDiagnosticReportProviderOrganizationId}
                        initialPractitionerId={this.props.request.selectedDiagnosticReportProviderPractitionerId}
                        onChange={async (organizationId, practitionerId) => {
                            await this.handleChange('selectedDiagnosticReportProviderOrganizationId', organizationId);
                            await this.handleChange('selectedDiagnosticReportProviderPractitionerId', practitionerId);

                            await this.getServiceOrganizationSettings(organizationId);
                        }}
                        onDoValidate={async () =>
                            await this.props.validateOne('selectedDiagnosticReportProviderOrganizationId')
                        }
                        requesterOrganizationId={this.props.organizationId}
                        serviceCode={this.props.service.code}
                        serviceProviderServiceId={this.props.service.id}
                        serviceType={this.props.service.serviceType}
                    />

                    {this.props.request.customControlData.somnologyOrganizationType ===
                        SomnologyOrganizationType.ksyosMedicalCenter && (
                        <ReferralAddEditKsyosMedicalCenter
                            {...this.props}
                            customControlData={this.props.request.customControlData}
                            serviceId={this.props.service.id}
                        />
                    )}

                    <PatientContactAddEdit
                        addressMandatory
                        showAddress={
                            this.props.request.customControlData.somnologyOrganizationType ===
                            SomnologyOrganizationType.ksyosMedicalCenter
                        }
                        {...this.props}
                    />

                    <FormDivider />

                    <MedicationsAddEdit
                        entity={ProcessEntity.request}
                        entityId={this.props.request.id}
                        errors={this.props.errors}
                        handleChangePatientUsesMedication={async (patientUsesMedication: boolean) =>
                            await this.props.handleChange('patientUsesMedication', patientUsesMedication)
                        }
                        onChange={async (medications: IMedication[]) =>
                            await this.props.handleChange('requestMedications', medications)
                        }
                        patientUsesMedication={this.props.request.patientUsesMedication}
                        validateMedications={async () => await this.props.validateOne('requestMedications')}
                        validatePatientUsesMedication={async () =>
                            await this.props.validateOne('patientUsesMedication')
                        }
                    />

                    <ProfessionalSummaryData
                        entity={ProcessEntity.request}
                        entityId={this.props.request.id}
                        errors={this.props.errors}
                        handleChangeAllergies={async (allergies) =>
                            await this.props.handleChange('allergies', allergies)
                        }
                        handleChangeCustomControl={this.props.handleChangeCustomControl}
                        handleChangeEpisodes={async (episodesAndJournal) =>
                            await this.props.handleChange('requestEpisodesAndJournal', episodesAndJournal)
                        }
                        handleChangeLabResults={async (labResults) =>
                            await this.props.handleChange('requestLabResults', labResults)
                        }
                        validateEpisodesAndJournals={async () =>
                            await this.props.validateOne('requestEpisodesAndJournal')
                        }
                        validateOneCustomControl={this.props.validateOneCustomControl}
                    />

                    {this.props.request.customControlData.somnologyOrganizationType !==
                        SomnologyOrganizationType.ksyosMedicalCenter && (
                        <FormSection type="referral">
                            <HeaderDivider text="Bereidheid medisch consult" />
                            <HorizontalFormGroup hasErrors={this.props.errors.has('toestemmingVideoConsult')}>
                                <InputControl isFormControl>
                                    <CheckboxInput
                                        automation="toestemmingVideoConsult"
                                        checked={this.props.request.customControlData.toestemmingVideoConsult || false}
                                        error={this.props.errors.get('toestemmingVideoConsult')}
                                        onChange={async (value) =>
                                            await this.props.handleChangeCustomControl({
                                                toestemmingVideoConsult: value,
                                            })
                                        }
                                        onDoValidate={async () => {
                                            if (this.props.errors.has('toestemmingVideoConsult')) {
                                                await this.props.validateOneCustomControl('toestemmingVideoConsult');
                                            }
                                        }}
                                        text={
                                            'De patiënt weet en begrijpt dat een medisch consult met de specialist noodzakelijk is. De patiënt is bereid en in staat dit door middel van een videoconsult te doen of om naar een locatie in de regio te reizen.'
                                        }
                                    />
                                </InputControl>
                            </HorizontalFormGroup>
                        </FormSection>
                    )}

                    {this.props.request.customControlData.somnologyOrganizationType ===
                        SomnologyOrganizationType.ksyosMedicalCenter && (
                        <>
                            <ProcessItemHeader
                                isFirstItem={false}
                                isItemOpen
                                process={this.props.process}
                                title={getServiceLabel(ServiceCode.OSASC1, 'request') ?? ''}
                            />

                            <Osasc1V1DiagnosticReportAddEditCustomControlFields
                                {...this.props}
                                customControlData={this.props.request.customControlData.consult}
                                errors={renameErrorKeys(
                                    this.props.errors,
                                    'customControlData.consult',
                                    'customControlData'
                                )}
                                handleChangeCustomControl={async (value: Partial<IOsasc1CustomControlData>) =>
                                    await this.props.handleChangeCustomControl({
                                        consult: { ...this.props.request.customControlData.consult, ...value },
                                    })
                                }
                                request={this.props.request}
                            />
                        </>
                    )}

                    <OsasvV1FollowingSteps />

                    <ButtonContainer
                        alignRight
                        buttons={[
                            <CancelButton
                                buttonText={translateKey('common.cancel')}
                                cancel={this.props.onCancel}
                                entity={ProcessEntity.request}
                                isExisting={!!this.props.request.id}
                                serviceCode={this.props.service.code}
                                serviceType={this.props.service.serviceType}
                            />,
                            <SaveButton
                                buttonDisabled={this.state.buttonDisabled}
                                save={async () => {
                                    await this.saveRequest('draft');
                                }}
                            />,
                            <SaveSendButton
                                buttonDisabled={this.state.buttonDisabled}
                                saveSend={async () => {
                                    await this.saveRequest('final');
                                }}
                            />,
                        ]}
                    />

                    <ContinueWithoutMedicationsModal
                        handleNo={async () =>
                            await this.setState({ proceedWithoutMedications: false, buttonDisabled: false })
                        }
                        handleYes={async () => await this.saveRequest('final')}
                        isOpen={!!this.state.proceedWithoutMedications}
                    />
                </Form>
            </NewOrEditRequestProcessContainer>
        );
    }

    private async getServiceOrganizationSettings(organizationId: string | null) {
        if (!organizationId) {
            return await this.props.handleChangeCustomControl({
                somnologyOrganizationType: null,
                relevante_klachten: null,
            });
        }
        const url = `/api/serviceOrganizationSettings?serviceId=${this.props.service.id}&organizationId=${organizationId}&isActive=true`;
        const result = await fetchJson(url, 'GET');
        let somnologyOrganizationType: SomnologyOrganizationType | null = null;
        if (result.json.items?.length === 1) {
            const settings: IServiceOrganizationSettings = result.json.items[0];
            somnologyOrganizationType = settings.somnologyOrganizationType ?? null;
        }

        if (this.props.request.customControlData.somnologyOrganizationType === somnologyOrganizationType) {
            return;
        }

        if (somnologyOrganizationType === SomnologyOrganizationType.ksyosMedicalCenter) {
            await this.props.handleChangeCustomControl({
                somnologyOrganizationType,
                relevante_klachten: null,
                verwijsreden: 'Vermoeden OSAS of uitsluiten OSAS',
                consult: { ...osasc1V1DiagnosticReportInitializeCustomControlData(), somnologyOrganizationType },
                typeSlaaprecorder: null,
                slaaprecorderVerzenden: null,
            });
        } else {
            await this.props.handleChangeCustomControl({
                somnologyOrganizationType,
                relevante_klachten: klachtenNsi(),
                hoofdklacht: null,
                verwijsreden: 'Vermoeden OSAS',
                consult: null,
                typeSlaaprecorder: null,
                slaaprecorderVerzenden: null,
            });
        }
    }

    private async handleChange<K extends keyof IRequestAddEdit>(name: K, value: IRequestAddEdit[K]) {
        await this.props.handleChange(name, value);
    }

    private async saveRequest(saveType: 'draft' | 'final') {
        await this.setState({ buttonDisabled: true });

        const validatedRequest = await this.props.validateRequest(saveType);
        if (!validatedRequest) {
            await this.setState({ buttonDisabled: false });

            return;
        }

        if (
            !this.state.proceedWithoutMedications &&
            (!this.props.request.requestMedications || this.props.request.requestMedications.length === 0) &&
            (this.props.request.patientUsesMedication ? YesOrNo.yes : YesOrNo.no) === YesOrNo.yes &&
            saveType === 'final'
        ) {
            await this.setState({ proceedWithoutMedications: true });

            return;
        }

        const requestId = await this.props.onSaveRequest(saveType);

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                this.props.service,
                'request',
                translateKey('toast.submitDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                this.props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }

        if (this.props.reloadPatient) {
            await this.props.reloadPatient();
        }

        await this.props.onSave(requestId);
        if (this.props.reloadProcess) {
            await this.props.reloadProcess(ReloadEpisode.reloadAll);
        }
        if (this.props.reloadPatient) {
            await this.props.reloadPatient();
        }
    }
}

export function osasvV1RequestValidateOneCustomControlData(
    name: string,
    customControlData: ICustomControlDataOsasv,
    errors: ValidationErrors
) {
    if (name === 'relevante_klachten') {
        return validateRelevanteKlachten(customControlData, errors);
    } else if (name === 'toestemmingVideoConsult') {
        return validateToestemmingVideoConsult(customControlData, errors);
    } else if (
        customControlData.somnologyOrganizationType === SomnologyOrganizationType.ksyosMedicalCenter &&
        customControlData.consult &&
        (name === 'comorbiditeiten' ||
            (name === 'comorbiditeitenChoice' &&
                customControlData.consult.comorbiditeitenChoice === ComorbiditiesChoice.nee))
    ) {
        return validateComorbiditeiten(customControlData.consult, errors);
    }

    return errors;
}

export function osasvV1RequestValidateCustomControlData(
    customControlData: ICustomControlDataOsasv,
    errors: ValidationErrors
) {
    errors = validateRelevanteKlachten(customControlData, errors);

    if (customControlData.somnologyOrganizationType === SomnologyOrganizationType.ksyosMedicalCenter) {
        errors = validateComorbiditeiten(customControlData.consult, errors);
    }

    return validateToestemmingVideoConsult(customControlData, errors);
}

function validateComorbiditeiten(customControlData: any, errors: ValidationErrors) {
    if (
        customControlData.comorbiditeitenChoice === ComorbiditiesChoice.ja &&
        customControlData.comorbiditeiten.anders_checked &&
        !customControlData.comorbiditeiten.anders_text
    ) {
        return errors.set('customControlData.comorbiditeiten', 'any_required');
    } else if (
        customControlData.comorbiditeitenChoice === ComorbiditiesChoice.ja &&
        !Object.values(customControlData.comorbiditeiten).some((value) => value)
    ) {
        return errors.set('customControlData.comorbiditeiten', 'any_selected');
    } else {
        return errors.remove('customControlData.comorbiditeiten');
    }
}

function validateRelevanteKlachten(customControlData: ICustomControlDataOsasv, errors: ValidationErrors) {
    if (
        customControlData.somnologyOrganizationType === SomnologyOrganizationType.ksyosMedicalCenter ||
        !customControlData.relevante_klachten
    ) {
        return errors.remove('relevante_klachten');
    }

    const relevanteKlachtenWasFilledIn = Object.values(customControlData.relevante_klachten).some((value) => {
        return value !== false && value !== null;
    });

    return relevanteKlachtenWasFilledIn
        ? errors.remove('relevante_klachten')
        : errors.set('relevante_klachten', 'any_required');
}

function validateToestemmingVideoConsult(customControlData: ICustomControlDataOsasv, errors: ValidationErrors) {
    if (customControlData.somnologyOrganizationType === SomnologyOrganizationType.ksyosMedicalCenter) {
        return errors.remove('toestemmingVideoConsult');
    }

    return customControlData.toestemmingVideoConsult
        ? errors.remove('toestemmingVideoConsult')
        : errors.set('toestemmingVideoConsult', 'permission_required');
}

function klachtenNsi() {
    return {
        vermoeidheid_overdag: false,
        verminderde_concentratie: false,
        stokkende_ademhaling_tijdens_de_slaap: false,
        veel_snurken: false,
        niet_verfrissende_slaap: false,
        meer_dan_5_ademstops_per_uur: false,
        andersText: null,
    };
}

export function osasvV1InitializeCustomControlData(): ICustomControlDataOsasv {
    return {
        toestemmingVideoConsult: false,
        somnologyOrganizationType: null,
        verwijsreden: 'Vermoeden OSAS',
        relevante_klachten: null,
        voorgeschiedenis: null,
        slaapapneu_in_de_1e_graad: null,
        contactgegevens_patient: {
            emailadres: null,
            mobileNumber: null,
            homePhoneNumber: null,
            street: null,
            houseNumberSuffix: null,
            houseNumber: null,
            zip: null,
            city: null,
            country: null,
        },
        osasReferralPermission: {
            patientPermission: false,
            patientQuestionnaireEmail: false,
        },
        patientHasSmartphone: null,
        typeSlaaprecorder: null,
        slaaprecorderVerzenden: null,
        hoofdklacht: null,
        consult: null,
    };
}
